export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  MECHANIC = 'mechanic',
}

export enum AppointmentStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  READY_FOR_PICKUP = 'ready_for_pickup',
  CLOSED_OUT = 'closed_out',
  CANCELLED = 'cancelled',
}

export enum TaskStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FINISHED = 'finished',
  CANCELLED = 'cancelled',
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  shopId?: string | null;
}

export interface Shop {
  id: string;
  name: string;
  address: string;
  phoneNumber?: string;
  email?: string;
  description?: string;
  isActive: boolean;
  /**
   * IANA zone of the shop, on the public shop reads. Anonymous pages (booking,
   * tracking) cannot read shop settings, so this is how they get the shop's
   * clock rather than the visitor's.
   */
  timezone?: string;
  /** The shop's weekly hours, on the public shop reads. */
  businessHours?: Record<DayOfWeek, BusinessHour>;
}

export interface Mechanic {
  id: string;
  userId: string;
  shopId: string;
  specialization: string;
  yearsOfExperience: number;
  isAvailable: boolean;
  user?: User;
  shop?: Shop;
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  appointmentId: string;
  assignedToId?: string;
  assignedTo?: User;
  createdAt: string;
  updatedAt: string;
}

export enum InvoiceStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  PAID = 'paid',
}

export interface TaxComponent {
  id: string;
  taxTypeId: string;
  name: string;
  /** Fraction, not a percentage: 0.0625 === 6.25%. May exceed 1. */
  rate: number | string;
  sortOrder: number;
}

export interface TaxTypePayload {
  name: string;
  shopId?: string;
  isDefault?: boolean;
  components: { name: string; rate: number }[];
}

export interface TaxType {
  id: string;
  shopId: string;
  name: string;
  components?: TaxComponent[];
  isDefault: boolean;
  isActive: boolean;
}

/** One tax component as charged on an invoice, snapshotted at its rate. */
export interface InvoiceTax {
  id: string;
  invoiceId: string;
  name: string;
  rate: number | string;
  amount: number | string;
  sortOrder: number;
}

export interface InvoiceItem {
  id: string;
  invoiceId: string;
  description: string;
  // MySQL DECIMAL columns can arrive as strings; the backend coerces them, but
  // the wider type keeps callers honest about running Number() first.
  quantity: number | string;
  unitPrice: number | string;
  taxTypeId?: string | null;
  /** The stocked part this line sells; issuing the invoice takes it out of stock. */
  partId?: string | null;
  sortOrder: number;
}

// Keep in sync with mechanix-portal/src/payments/payment.model.ts.
export enum PaymentProvider {
  EXTERNAL = 'external',
  SQUARE_ONLINE = 'square_online',
  SQUARE_TERMINAL = 'square_terminal',
  STRIPE_ONLINE = 'stripe_online',
  STRIPE_TERMINAL = 'stripe_terminal',
}

export enum PaymentMethod {
  CASH = 'cash',
  CHECK = 'check',
  CARD = 'card',
  OTHER = 'other',
}

/** Square's spelling: `canceled`, one L — unlike AppointmentStatus.CANCELLED. */
export enum PaymentStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELED = 'canceled',
  REFUNDED = 'refunded',
}

export interface Payment {
  id: string;
  invoiceId: string;
  shopId: string;
  provider: PaymentProvider;
  method?: PaymentMethod | null;
  status: PaymentStatus;
  amount: number | string;
  /**
   * How much of `amount` has been given back — a refund is not its own ledger
   * row. Same `number | string` caution as `amount`: DECIMAL columns arrive as
   * strings unless the model declares a getter.
   */
  refundedAmount?: number | string;
  providerRefundId?: string | null;
  currency: string;
  note?: string | null;
  failureReason?: string | null;
  /** Epoch seconds — when the money actually arrived. */
  receivedAt?: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Reduced view of a shop's Square connection — tokens never leave the server. */
export interface SquareConnectionStatus {
  connected: boolean;
  /** False when the platform administrator switched Square off for the shop. */
  allowed?: boolean;
  /** False when the server has no Square app credentials at all. */
  configured?: boolean;
  revoked?: boolean;
  merchantId?: string;
  locationId?: string | null;
  locationName?: string | null;
  deviceId?: string | null;
  environment?: 'sandbox' | 'production';
  needsLocation?: boolean;
}

export interface SquareLocation {
  id: string;
  name: string;
  status?: string;
}

/**
 * The public pay page's reduced invoice view — keep in sync with
 * mechanix-portal/src/payments/payments.service.ts (PublicInvoiceView).
 */
export interface PublicInvoiceView {
  id: string;
  ref: string;
  status: InvoiceStatus;
  shop: { name?: string; address?: string; phoneNumber?: string };
  vehicle?: string;
  customerFirstName?: string;
  items: {
    description: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
  }[];
  subtotal: number;
  taxes: { name: string; rate: number; amount: number }[];
  amountDue: number;
  amountPaid: number;
  balanceDue: number;
  payment: {
    enabled: boolean;
    /** Which card form to render. */
    provider?: 'square' | 'stripe';
    // Square Web Payments SDK
    applicationId?: string;
    locationId?: string;
    environment?: 'sandbox' | 'production';
    // Stripe.js
    publishableKey?: string;
  };
}

export interface Invoice {
  id: string;
  appointmentId: string;
  items?: InvoiceItem[];
  /** One row per distinct tax component charged. */
  taxes?: InvoiceTax[];
  /** The payment ledger; `status` is derived from these server-side. */
  payments?: Payment[];
  subtotal: number | string;
  taxAmount: number | string;
  /** Grand total: subtotal + taxAmount. */
  amountDue: number | string;
  /** Server-derived sums — present when payments were included. */
  amountPaid?: number;
  balanceDue?: number;
  notes?: string;
  status: InvoiceStatus;
  generatedAt: string;
  sentAt?: string | null;
  paidAt?: string | null;
  updatedAt: string;
  appointment?: Appointment;
}

export interface Appointment {
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  vehicleModel: string;
  vehicleYear: string;
  /** Optional; the API stores it upper case. */
  licensePlate?: string | null;
  serviceDescription: string;
  comments?: string;
  appointmentDate?: number; // Now an epoch timestamp
  appointmentTime: string;
  appointmentDateTime?: number; // Now an epoch timestamp
  status: AppointmentStatus;
  shopId: string;
  mechanicId?: string;
  mechanic?: Mechanic;
  shop?: Shop;
  tasks?: Task[];
  /** The bay the job is in, 1-based; null when unassigned. */
  bay?: number | null;
  appointmentDateObj?: Date;
}

/**
 * GET /appointments/shop/:shopId/floor — the shop's bays and the jobs holding
 * one (in progress or completed, until the invoice moves them on).
 */
export interface WorkshopFloor {
  /** 0 until the shop sets its bays up in settings. */
  bayCount: number;
  jobs: Appointment[];
}

// Reduced, public-safe view returned by GET /appointments/track/:id.
// Deliberately excludes customer contact details and staff accounts.
export interface AppointmentTracking {
  id: string;
  /** Used to look up bookable slots when the customer reschedules. */
  shopId: string;
  /** IANA zone of the shop: times are shown and picked on this clock. */
  timezone: string;
  status: AppointmentStatus;
  appointmentDateTime: number;
  customerName: string;
  vehicleModel: string;
  vehicleYear: string;
  serviceDescription: string;
  comments: string | null;
  shop: { name: string; address: string; phoneNumber: string | null } | null;
  mechanic: { name: string; specialization: string | null } | null;
  tasks: { id: string; title: string; status: TaskStatus }[];
}

export interface CreateAppointmentDto {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  vehicleModel: string;
  vehicleYear: string;
  licensePlate?: string | null;
  serviceDescription: string;
  comments?: string;
  // Epoch seconds. The only time the API accepts — it derives the legacy
  // appointmentDate/appointmentTime mirrors from this itself, in the shop's
  // timezone, so a client can no longer file a booking under another date.
  appointmentDateTime: number;
  shopId: string;
  // Staff-only: honoured when the request carries a token for the shop, and
  // ignored for a customer booking from the public form.
  mechanicId?: string;
  status?: AppointmentStatus;
}

export interface UpdateAppointmentDto {
  status?: AppointmentStatus;
  mechanicId?: string;
  /** Blank or null clears it. */
  licensePlate?: string | null;
  /** 1-based; null takes the job out of its bay. */
  bay?: number | null;
}

export interface CreateTaskDto {
  title: string;
  description?: string;
  status?: TaskStatus;
  appointmentId: string;
  assignedToId?: string;
}

export interface UpdateTaskDto {
  title?: string;
  description?: string;
  status?: TaskStatus;
  assignedToId?: string;
}

export interface CreateMechanicDto {
  userId: string;
  shopId: string;
  specialization: string;
  yearsOfExperience: number;
  isAvailable?: boolean;
}

export interface UpdateMechanicDto {
  specialization?: string;
  yearsOfExperience?: number;
  isAvailable?: boolean;
}

export interface CreateShopDto {
  name: string;
  address: string;
  phoneNumber?: string;
  email?: string;
  description?: string;
}

export interface UpdateShopDto {
  name?: string;
  address?: string;
  phoneNumber?: string;
  email?: string;
  description?: string;
  isActive?: boolean;
}

export interface LoginDto {
  email: string;
  password: string;
  shopId?: string;
}

export interface RegisterDto {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  shopId?: string;
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

export interface CreateMechanicWithUserDto {
  name: string;
  email: string;
  password: string;
  specialization: string;
  yearsOfExperience: number;
  isAvailable?: boolean;
  shopId: string;
}

// Shop Settings
export interface BusinessHour {
  open: string | null;
  close: string | null;
}

export interface Holiday {
  date: string;
  description: string;
}

export interface SpecialHours {
  date: string;
  open: string | null;
  close: string | null;
  description: string;
}

export enum DayOfWeek {
  SUNDAY = 'SUNDAY',
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
}

export interface ShopSettings {
  id: string;
  shopId: string;
  timeSlotSize: number;
  maxConcurrentBookings: number;
  /** Service bays on the workshop floor; 0 = not set up yet. */
  bayCount: number;
  timezone: string;
  businessHours: Record<DayOfWeek, BusinessHour>;
  holidays: Holiday[];
  specialHours: SpecialHours[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateShopSettingsDto {
  shopId: string;
  timeSlotSize: number;
  maxConcurrentBookings: number;
  timezone: string;
  businessHours: Record<DayOfWeek, BusinessHour>;
  holidays: Holiday[];
  specialHours: SpecialHours[];
}

export interface UpdateShopSettingsDto {
  timeSlotSize?: number;
  maxConcurrentBookings?: number;
  bayCount?: number;
  timezone?: string;
  businessHours?: Record<DayOfWeek, BusinessHour>;
  holidays?: Holiday[];
  specialHours?: SpecialHours[];
} 

/**
 * Dashboard aggregates from `GET /api/reports/summary`.
 *
 * Money comes from the payment ledger, never from an invoice status flag —
 * `collected` is completed payments received in the window, `outstanding` is
 * what issued invoices still owe regardless of window.
 */
export interface DashboardSummary {
  range: { from: number; to: number };
  /** IANA zone the daily buckets are keyed in. */
  timezone: string;
  appointments: {
    total: number;
    byStatus: Record<string, number>;
    /** Percent finished, excluding cancellations. */
    completionRate: number;
  };
  revenue: {
    collected: number;
    invoiced: number;
    outstanding: number;
    averageTicket: number;
  };
  mechanics: {
    id: string;
    name: string;
    assigned: number;
    completed: number;
  }[];
  daily: {
    /** Shop-local "YYYY-MM-DD". */
    date: string;
    appointments: number;
    revenue: number;
  }[];
}


/**
 * A shop's repeatable services. A *template*, not a link: pricing an invoice
 * line copies these values onto the line, so editing a catalogue price never
 * reprices an invoice that has already been issued.
 */
export interface ServiceCatalogItem {
  id: string;
  shopId: string;
  name: string;
  description?: string | null;
  /** DECIMAL — may arrive as a string. */
  defaultPrice: number | string;
  defaultQuantity: number | string;
  taxTypeId?: string | null;
  taxType?: TaxType | null;
  isActive: boolean;
  sortOrder: number;
}

export interface ServiceCatalogItemPayload {
  name: string;
  description?: string;
  defaultPrice?: number;
  defaultQuantity?: number;
  taxTypeId?: string;
  isActive?: boolean;
  sortOrder?: number;
}

// ---------------------------------------------------------------------------
// Integrations — outbound webhooks and provider connections. Mirrors
// mechanix-portal/src/integrations/. Timestamps are epoch seconds; BIGINT may
// arrive as a string, so read them through Number().
// ---------------------------------------------------------------------------

export interface IntegrationEventTypeInfo {
  type: string;
  description: string;
}

export interface IntegrationEventCatalog {
  apiVersion: string;
  events: IntegrationEventTypeInfo[];
}

export interface WebhookEndpoint {
  id: string;
  shopId: string;
  url: string;
  description: string | null;
  /** Subscribed event types, or ["*"] for all. */
  events: string[];
  isActive: boolean;
  disabledReason: string | null;
  createdAt: number | string;
  updatedAt: number | string;
}

export interface WebhookEndpointPayload {
  url: string;
  description?: string;
  events: string[];
  isActive?: boolean;
}

/** Returned on create and rotate only — the secret cannot be read back. */
export interface WebhookEndpointWithSecret {
  endpoint: WebhookEndpoint;
  secret: string;
}

export type WebhookDeliveryStatus = 'pending' | 'delivered' | 'failed';

export interface WebhookDelivery {
  id: string;
  eventId: string;
  status: WebhookDeliveryStatus;
  attempts: number;
  nextAttemptAt: number | string;
  lastStatusCode: number | null;
  lastError: string | null;
  deliveredAt: number | string | null;
  createdAt: number | string;
  event?: { id: string; type: string; occurredAt: number | string };
}

export interface IntegrationConnection {
  id: string;
  shopId: string;
  provider: string;
  status: 'active' | 'error' | 'disconnected';
  externalAccountId: string | null;
  lastError: string | null;
  connectedAt: number | string | null;
}

// ---------------------------------------------------------------------------
// Stripe, parts suppliers and inventory. Mirrors mechanix-portal/src/payments
// (Stripe) and mechanix-portal/src/inventory. DECIMAL and BIGINT columns may
// arrive as strings; read them through Number().
// ---------------------------------------------------------------------------

export interface StripeConnectionStatus {
  connected: boolean;
  /** False when the platform administrator switched Stripe off for the shop. */
  allowed: boolean;
  status?: 'active' | 'error' | 'disconnected';
  mode?: 'test' | 'live';
  accountId?: string;
  accountName?: string | null;
  publishableKey?: string;
  readerId?: string | null;
  primary: boolean;
  webhookConfigured: boolean;
  webhookUrl: string;
  requiredEvents: string[];
  lastError?: string | null;
}

export interface StripeIntent {
  paymentId: string;
  clientSecret: string;
  publishableKey: string;
}

export interface SupplierCredentialField {
  key: string;
  label: string;
  secret: boolean;
  help?: string;
}

export interface SupplierStatus {
  provider: string;
  displayName: string;
  description: string;
  modes: ('api' | 'punchout')[];
  credentialFields: SupplierCredentialField[];
  available: boolean;
  /** False when the platform administrator switched it off for this shop. */
  allowed: boolean;
  connected: boolean;
  status: 'active' | 'error' | 'disconnected' | null;
  externalAccountId: string | null;
  lastError: string | null;
}

export interface SupplierAvailability {
  locationId: string;
  locationName: string;
  quantity: number;
  eta?: string;
}

export interface SupplierQuote {
  provider: string;
  supplierName: string;
  supplierPartNumber: string;
  brand: string;
  description: string;
  unitCost: number;
  listPrice: number | null;
  coreCharge: number | null;
  availability: SupplierAvailability[];
  quoteRef?: string;
}

export interface PartsSearchResponse {
  results: SupplierQuote[];
  errors: { provider: string; message: string }[];
}

export interface Part {
  id: string;
  shopId: string;
  sku: string;
  partNumber: string;
  brand: string | null;
  name: string;
  description: string | null;
  unitCost: number | string;
  unitPrice: number | string;
  coreCharge: number | string;
  taxTypeId: string | null;
  taxType?: TaxType | null;
  onHand: number | string;
  reorderPoint: number | string;
  reorderQuantity: number | string;
  binLocation: string | null;
  preferredSupplier: string | null;
  isActive: boolean;
}

export interface PartPayload {
  sku?: string;
  partNumber: string;
  brand?: string;
  name: string;
  description?: string;
  unitCost?: number;
  unitPrice?: number;
  coreCharge?: number;
  taxTypeId?: string;
  reorderPoint?: number;
  reorderQuantity?: number;
  binLocation?: string;
  preferredSupplier?: string;
  openingStock?: number;
  isActive?: boolean;
}

export type StockMovementType = 'receive' | 'consume' | 'adjust' | 'return' | 'import';

export interface StockMovement {
  id: string;
  partId: string;
  type: StockMovementType;
  quantity: number | string;
  unitCost: number | string | null;
  balanceAfter: number | string;
  refType: string | null;
  refId: string | null;
  note: string | null;
  occurredAt: number | string;
}

export enum PurchaseOrderStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  CONFIRMED = 'confirmed',
  PARTIALLY_RECEIVED = 'partially_received',
  RECEIVED = 'received',
  CANCELLED = 'cancelled',
  FAILED = 'failed',
}

export interface PurchaseOrderLine {
  id: string;
  purchaseOrderId: string;
  partId: string | null;
  supplierPartNumber: string;
  brand: string | null;
  description: string;
  quantity: number | string;
  quantityReceived: number | string;
  unitCost: number | string;
  listPrice: number | string | null;
  coreCharge: number | string;
  supplierLocation: string | null;
}

export interface PurchaseOrder {
  id: string;
  shopId: string;
  poNumber: string;
  supplierProvider: string;
  supplierName: string | null;
  status: PurchaseOrderStatus;
  appointmentId: string | null;
  externalOrderId: string | null;
  notes: string | null;
  subtotal: number | string;
  lastError: string | null;
  submittedAt: number | string | null;
  receivedAt: number | string | null;
  createdAt: number | string;
  lines?: PurchaseOrderLine[];
}

export interface PurchaseOrderLinePayload {
  partId?: string;
  supplierPartNumber: string;
  brand?: string;
  description: string;
  quantity: number;
  unitCost: number;
  listPrice?: number;
  coreCharge?: number;
  supplierLocation?: string;
  locationId?: string;
  quoteRef?: string;
}

export interface PriceImportResult {
  rowsRead: number;
  created: number;
  updated: number;
  unchanged: number;
  stockAdjusted: number;
  skipped: { line: number; reason: string }[];
  columns: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Platform integration switches (super admin). Mirrors
// mechanix-portal/src/integrations/access/.
// ---------------------------------------------------------------------------

export type IntegrationCategory = 'payments' | 'parts' | 'platform';

/** Well-known keys; supplier integrations use their provider key. */
export const INTEGRATION_KEYS = {
  STRIPE: 'stripe',
  SQUARE: 'square',
  WEBHOOKS: 'webhooks',
  PRICE_IMPORT: 'price_import',
} as const;

/** What a shop admin sees: which integrations the platform allows. */
export interface ShopIntegrationAccess {
  key: string;
  name: string;
  category: IntegrationCategory;
  description: string;
  enabled: boolean;
  overridden: boolean;
  reason: string | null;
  updatedAt: number | null;
  serverConfigured: boolean;
}

export interface IntegrationColumn {
  key: string;
  name: string;
  category: IntegrationCategory;
  description: string;
  defaultEnabled: boolean;
  serverConfigured: boolean;
}

export interface IntegrationCell {
  enabled: boolean;
  overridden: boolean;
  connected: boolean;
  reason: string | null;
  updatedAt: number | null;
}

export interface IntegrationMatrix {
  integrations: IntegrationColumn[];
  shops: {
    id: string;
    name: string;
    isActive: boolean;
    access: Record<string, IntegrationCell>;
  }[];
}
